import '@/theme/partials/_button.scss';

import {
  IonButton, IonButtons, IonIcon, IonPage, IonSpinner, IonText,
} from '@ionic/react';
import {
  BarElement,
  CategoryScale,
  Chart,
  Chart as ChartJS,
  ChartOptions,
  LinearScale,
} from 'chart.js';
import classNames from 'classnames';
import dayjs from 'dayjs';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import ArrowDown from '@/assets/icons/liquidation-arrow-down.svg';
import ArrowUp from '@/assets/icons/liquidation-arrow-up.svg';
import Receipt from '@/assets/icons/receipt.svg';
import ArrowLeft from '@/assets/icons/selector-left-arrow.svg';
import ArrowRight from '@/assets/icons/selector-right-arrow.svg';
import emptyStateImg from '@/assets/imgs/empty-reporting.svg';
import Banner from '@/components/common/Banner';
import { BannerType } from '@/components/common/Banner/Banner';
import { fullMonths, ROUTES_PATH } from '@/constants';
import {
  MonthSummary,
  SettlementsAPIData,
} from '@/interfaces/ReportingInterface/ReportingInterface';
import { fetchMonthSummaries, saveSettlements, selectCurrentSettlements } from '@/redux/reporting/reporting';
import { getSettlements } from '@/redux/reporting/reportingAPI';
import { RootState } from '@/store';
import { useAppDispatch } from '@/store/hooks';
import { percentageHelper, pricesHelper } from '@/utils/pricesHelper';
import { getMonthDays } from '@/utils/settlementsHelper';

import styles from './Reporting.module.scss';

const Reporting = () => {
  const labels = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun'];
  const monthLabelsList = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ];

  const currentMonth = parseInt(dayjs().format('M'));
  const chartRef = useRef<Chart<'bar'>>();

  const configDatasets = [
    {
      data: [5, 10, 4, 7, 2, 9],
      barPercentage: 1,
      borderRadius: 8,
      hoverBackgroundColor(context: any) {
        return selectedBarIdx.current === context.dataIndex ? '#FF5C5C' : '#FFEBEB';
      },
    },
  ];

  const initialChartData = {
    labels,
    datasets: configDatasets,
  };
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [chartData, setChartData] = useState<{ labels: any; datasets: any }>(
    initialChartData,
  );

  const [isLoading, setisLoading] = useState<boolean>(false);
  const [emptyData, setEmptyData] = useState<boolean>(true);
  const [merchantSalesType] = useState<string>(sessionStorage.getItem('merchantType') || '');

  const [availableDates, setAvailableDates] = useState<any>([]);
  const [availableMonths, setAvailableMonths] = useState<any>([]);
  const [monthsByYear, setMonthsByYear] = useState<any>({});
  const [uniqueYears, setUniqueYears] = useState<any>([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [loadingData, setLoadingData] = useState<boolean>(false);

  const storedMonthSummaries = useSelector(
    (state: RootState) => state.settlement.data.monthSummaries,
  );
  const storedSettlements = useSelector(
    (state: RootState) => state.settlement.data.settlements,
  );
  const currentSettlements = useSelector(
    (state: RootState) => state.settlement.data.currentSettlements,
  );
  const [totalCharData, setTotalCharData] = useState({
    actualYear: '',
    actualMonthLabel: '',
    actualAmount: 0,
    previousMonthYear: '',
    previousMonthLabel: '',
    previousMonthAmount: 0,
    previousMonthPercentDiff: 0,
    previousYear: '',
    previousYearLabel: '',
    previousYearAmount: 0,
    previousYearPercentDiff: 0,
  });
  const [currentMonthSummary, setCurrentMonthSummary] = useState<MonthSummary | undefined>();
  const [currentChartRange, setCurrentChartRange] = useState({
    fromYear: 0,
    fromMonth: 0,
    fromIdx: 0,
    toYear: 0,
    toMonth: 0,
    toIdx: 0,
  });

  const selectedBarIdx = useRef<number>(4);

  useEffect(() => {
    if (storedMonthSummaries.length === 0) {
      setisLoading(true);
      dispatch(fetchMonthSummaries());
    }
  }, []);

  useEffect(() => {
    if (storedMonthSummaries.length > 0) {
      const existingData = storedMonthSummaries.find((e) => e.total_amount !== 0);
      if (!existingData) {
        setEmptyData(true);
        setisLoading(false);
      } else {
        setisLoading(true);
        setEmptyData(false);
        initData();
      }
    }
  }, [storedMonthSummaries]);

  useEffect(() => {
    const formattedDate = dayjs(`${selectedYear}-${fullMonths.indexOf(selectedMonth) + 1}`, 'YYYY-M').format('YYYY-MM');
    const paidSummary = storedMonthSummaries.find((e) => e.date === formattedDate);
    if (paidSummary) {
      setCurrentMonthSummary(paidSummary);
      loadStatsChartData(paidSummary);
      changeToMonthSummary(paidSummary);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (selectedYear && monthsByYear[selectedYear]) {
      setAvailableMonths(monthsByYear[selectedYear]);
      const filteredMonths = monthsByYear[selectedYear].find((d: any) => d.month === selectedMonth);
      if (filteredMonths && filteredMonths.length === 0) {
        setSelectedMonth(monthsByYear[selectedYear][monthsByYear[selectedYear].length - 1].month);
      }
    }
  }, [selectedYear, monthsByYear]);

  const checkDisabledYearOption = useCallback((year: string) => {
    const inRange = availableDates.find((el: any) => el.month === selectedMonth && el.year === year);
    if (inRange && inRange.length !== 0) {
      return false;
    }
    return true;
  }, [selectedMonth, availableDates]);

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 20,
    },
    plugins: {
      legend: {
        display: false,
        labels: {
          font: {
            family: "'Factor A', 'DM Sans', 'sans-serif'",
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: '#F1F0F0',
        },
        ticks: {
          font: {
            size: 14,
            weight: '600',
            family: "'Factor A', 'DM Sans', 'sans-serif'",
          },
        },
      },
      y: {
        grid: {
          display: true,
          color: '#F1F0F0',
        },
        ticks: {
          display: false,
        },
      },
    },
    hover: {
      mode: undefined,
    },
    events: ['click'],
  };

  ChartJS.register(CategoryScale, LinearScale, BarElement);

  const loadAvailableDates = () => {
    const currentDate = new Date();
    const allDates = [];
    const uniqueYearsSet = new Set();
    const monthsByYear: any = {};

    for (let i = 0; i < 18; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const year = date.getFullYear().toString();
      const month = fullMonths[date.getMonth()];

      allDates.push({ month, year });
      uniqueYearsSet.add(year);

      if (!monthsByYear[year]) {
        monthsByYear[year] = new Set();
      }
      monthsByYear[year].add({ month, year });
    }

    const uniqueYears = Array.from(uniqueYearsSet).sort().reverse();
    Object.keys(monthsByYear).forEach((year) => {
      monthsByYear[year] = Array.from(monthsByYear[year])
        .sort((a: any, b: any) => fullMonths.indexOf(a) - fullMonths.indexOf(b));
    });
    setAvailableDates(allDates);
    setUniqueYears(uniqueYears);
    setMonthsByYear(monthsByYear);
    setAvailableMonths(monthsByYear[selectedYear]);
    return allDates;
  };

  const handleMonthChange = (e: any) => {
    setSelectedMonth(availableMonths[e.target.selectedIndex].month);
    setSelectedYear(availableMonths[e.target.selectedIndex].year);
  };

  const handleYearChange = (e: any) => {
    setSelectedYear(e.target.value);
    const validMonth = availableDates.find((date: any) => date.year === e.target.value && date.month === selectedMonth)?.month;
    if (!validMonth) {
      const firstMonthForYear = availableDates.find((date: any) => date.year === e.target.value)?.month;
      setSelectedMonth(firstMonthForYear);
      loadAvailableDates();
    }
  };

  // Cargamos las fechas disponibles para los select y el rango inicial de meses a mostrar en la gráfica
  const initData = async () => {
    const allDates = loadAvailableDates();
    const initialMonths = selectChartMonths(currentMonth, 'prev');
    const chartNumbers = storedMonthSummaries.slice(0, 5).map((e) => e.total_amount).reverse();
    setChartData({
      labels: initialMonths,
      datasets: [
        {
          data: chartNumbers,
          barPercentage: 1,
          borderRadius: 4,
          backgroundColor(context: any) {
            return selectedBarIdx.current === context.dataIndex ? '#FF5C5C' : '#FFEBEB';
          },
        },
      ],
    });
    // setisLoading(false);
    setCurrentMonthSummary(storedMonthSummaries[0]);
    setSelectedMonth(allDates[0].month);
    setSelectedYear(allDates[0].year);
    loadStatsChartData(storedMonthSummaries[0], storedMonthSummaries);
    setCurrentChartRange({
      fromMonth: dayjs(storedMonthSummaries[4].date, 'YYYY-MM').month() + 1,
      fromYear: dayjs(storedMonthSummaries[4].date, 'YYYY-MM').year(),
      fromIdx: 4,
      toMonth: dayjs().month() + 1,
      toYear: dayjs().year(),
      toIdx: 0,
    });
    setTimeout(() => {
      setisLoading(false);
    }, 1000);
  };

  const changeToMonthSummary = (monthSummary: MonthSummary) => {
    const currentMonthSummaryIdx = storedMonthSummaries.findIndex((e) => e.date === monthSummary.date);
    let currentMonthsSlice = storedMonthSummaries.slice(currentChartRange.toIdx, currentChartRange.fromIdx + 1).reverse();
    const selectedMonthSliceIndex = currentMonthsSlice.findIndex((e) => e.date === monthSummary.date);

    // Comprobamos que al mes que estamos cambiando existe en el rango actual y en el total de meses que hemos obtenido de la API
    if (currentMonthSummaryIdx !== -1 && selectedMonthSliceIndex !== -1) {
      selectedBarIdx.current = currentMonthsSlice.findIndex((e) => e.date === monthSummary.date);
    } else {
      let outOfRange = false;
      const currentSummaryIdx = storedMonthSummaries.findIndex((e) => e.date === monthSummary.date);
      let monthsInRange = selectChartMonths(dayjs(monthSummary.date, 'YYYY-MM').month() + 1, 'next');
      let chartNumbers = [];

      currentMonthsSlice = storedMonthSummaries.slice(currentSummaryIdx - 4, currentSummaryIdx + 1).reverse();

      // Si el cálculo de los meses a mostrar está fuera de rango, mostraremos los 5 primeros meses del inicio
      if (currentMonthsSlice.length === 0) outOfRange = true;
      if (outOfRange) {
        currentMonthsSlice = storedMonthSummaries.slice(0, 5).reverse();
        chartNumbers = storedMonthSummaries.slice(0, 5).map((e) => e.total_amount);
        monthsInRange = selectChartMonths(dayjs(currentMonthsSlice[0].date, 'YYYY-MM').month() + 1, 'next');
      } else {
        chartNumbers = storedMonthSummaries.slice(currentSummaryIdx - 4, currentSummaryIdx + 1).map((e) => e.total_amount);
      }

      selectedBarIdx.current = currentMonthsSlice.findIndex((e) => e.date === monthSummary.date);
      setChartData({
        labels: monthsInRange,
        datasets: [
          {
            data: chartNumbers.reverse(),
            barPercentage: 1,
            borderRadius: 4,
            backgroundColor(context: any) {
              return selectedBarIdx.current === context.dataIndex ? '#FF5C5C' : '#FFEBEB';
            },
            hoverBackgroundColor(context: any) {
              return selectedBarIdx.current === context.dataIndex ? '#FF5C5C' : '#FFEBEB';
            },
          },
        ],
      });

      const firstMonthDate = dayjs(currentMonthsSlice[0].date, 'YYYY-MM');
      const lastMonthDate = dayjs(currentMonthsSlice[4].date, 'YYYY-MM');

      setCurrentChartRange({
        fromMonth: firstMonthDate.month() + 1,
        fromYear: firstMonthDate.year(),
        fromIdx: outOfRange ? 4 : currentSummaryIdx,
        toMonth: lastMonthDate.month() + 1,
        toYear: lastMonthDate.year(),
        toIdx: outOfRange ? 0 : currentSummaryIdx - 4,
      });
    }
  };

  const changeToPreviousMonth = async () => {
    if (currentMonthSummary) {
      const currentMonthSummaryIdx = storedMonthSummaries.findIndex((e) => e.date === currentMonthSummary.date);
      const previousMonthSummary = storedMonthSummaries[currentMonthSummaryIdx + 1];
      // Comprobamos que existe el mes previo
      if (previousMonthSummary) {
        setCurrentMonthSummary(previousMonthSummary);
        loadStatsChartData(previousMonthSummary); // Cargamos los datos de la tabla derecha

        const previousMonthSummaryDate = dayjs(previousMonthSummary.date, 'YYYY-MM');
        setSelectedYear(previousMonthSummaryDate.year().toString());
        setSelectedMonth(fullMonths[previousMonthSummaryDate.month()]);

        // Obtenemos los datos que estan en el rango actual
        const currentMonthsSlice = storedMonthSummaries.slice(currentChartRange.toIdx, currentChartRange.fromIdx + 1).reverse();
        // Comprobamos si el mes a mostrar está en el rango actual
        const newIndex = currentMonthsSlice.findIndex((e) => e.date === previousMonthSummary.date);
        if (newIndex !== -1) {
          selectedBarIdx.current = currentMonthsSlice.findIndex((e) => e.date === previousMonthSummary.date);
        } else if (storedMonthSummaries.length > 0) {
          // Si no lo está, cargamos el nuevo slice en la gráfica y establecemos el nuevo rango
          const previousMonthIdx = storedMonthSummaries.findIndex((e) => e.date === previousMonthSummary.date);
          const monthsInRange = selectChartMonths(dayjs(previousMonthSummary.date, 'YYYY-MM').month() + 1, 'next');
          const chartNumbers = storedMonthSummaries.slice(previousMonthIdx - 4, previousMonthIdx + 1).map((e) => e.total_amount).reverse();
          setChartData({
            labels: monthsInRange,
            datasets: [
              {
                data: chartNumbers,
                barPercentage: 1,
                borderRadius: 4,
                backgroundColor(context: any) {
                  return selectedBarIdx.current === context.dataIndex ? '#FF5C5C' : '#FFEBEB';
                },
              },
            ],
          });
          setCurrentChartRange({
            fromMonth: dayjs(currentMonthsSlice[0].date, 'YYYY-MM').month() + 1,
            fromYear: dayjs(currentMonthsSlice[0].date, 'YYYY-MM').year(),
            fromIdx: currentChartRange.fromIdx + 1,
            toMonth: dayjs(currentMonthsSlice[4].date, 'YYYY-MM').month() + 1,
            toYear: dayjs(currentMonthsSlice[4].date, 'YYYY-MM').year(),
            toIdx: currentChartRange.toIdx + 1,
          });
        }
      }
    }
  };

  // Mantenemos la misma lógica que en el cambio de mes anterior
  const changeToNextMonth = async () => {
    if (currentMonthSummary) {
      const currentMonthSummaryIdx = storedMonthSummaries.findIndex((e) => e.date === currentMonthSummary.date);
      if (storedMonthSummaries[currentMonthSummaryIdx - 1]) {
        setCurrentMonthSummary(storedMonthSummaries[currentMonthSummaryIdx - 1]);
        loadStatsChartData(storedMonthSummaries[currentMonthSummaryIdx - 1]);

        const currentMonthSummary = storedMonthSummaries[currentMonthSummaryIdx - 1];
        const currentSummaryDate = dayjs(currentMonthSummary.date, 'YYYY-MM');
        setSelectedYear(currentSummaryDate.year().toString());
        setSelectedMonth(fullMonths[currentSummaryDate.month()]);

        const currentMonthsSlice = storedMonthSummaries.slice(currentChartRange.toIdx, currentChartRange.fromIdx + 1).reverse();

        const newIndex = currentMonthsSlice.findIndex((e) => e.date === currentMonthSummary.date);
        if (newIndex !== -1) {
          selectedBarIdx.current = currentMonthsSlice.findIndex((e) => e.date === currentMonthSummary.date);
        } else if (storedMonthSummaries.length > 0) {
          const monthsInRange = selectChartMonths(dayjs(currentMonthSummary.date, 'YYYY-MM').month() + 1, 'prev');
          const chartNumbers = storedMonthSummaries.slice(currentChartRange.toIdx - 1, currentChartRange.fromIdx).map((e) => e.total_amount).reverse();
          setChartData({
            labels: monthsInRange,
            datasets: [
              {
                data: chartNumbers,
                barPercentage: 1,
                borderRadius: 4,
                backgroundColor(context: any) {
                  return selectedBarIdx.current === context.dataIndex ? '#FF5C5C' : '#FFEBEB';
                },
              },
            ],
          });
          setCurrentChartRange({
            fromMonth: dayjs(currentMonthsSlice[0].date, 'YYYY-MM').month() + 1,
            fromYear: dayjs(currentMonthsSlice[0].date, 'YYYY-MM').year(),
            fromIdx: currentChartRange.fromIdx - 1,
            toMonth: dayjs(currentMonthsSlice[4].date, 'YYYY-MM').month() + 1,
            toYear: dayjs(currentMonthsSlice[4].date, 'YYYY-MM').year(),
            toIdx: currentChartRange.toIdx - 1,
          });
        }
      }
    }
  };

  const loadStatsChartData = async (
    currentMonthSummary: MonthSummary,
    monthSummaries?: MonthSummary[],
  ) => {
    if (currentMonthSummary) {
      setCurrentMonthSummary(currentMonthSummary);
      const loadedMonthSummaries = monthSummaries || storedMonthSummaries;

      if (loadedMonthSummaries) {
        const currentDate = dayjs(currentMonthSummary.date, 'YYYY-MM');
        const previousMonthDate = currentDate.subtract(1, 'month');
        const previousYearDate = currentDate.subtract(1, 'year');
        const previousMonthSummary = loadedMonthSummaries.find((e) => e.date === previousMonthDate.format('YYYY-MM'));
        const previousYearMonthSummary = loadedMonthSummaries.find((e) => e.date === previousYearDate.format('YYYY-MM'));
        const data = {
          actualMonthLabel: fullMonths[currentDate.month()],
          actualAmount: currentMonthSummary.total_amount,
          actualYear: currentDate.format('YY'),
          previousMonthYear: currentDate.subtract(1, 'month').format('YY'),
          previousMonthLabel: fullMonths[currentDate.subtract(1, 'month').month()],
          previousMonthAmount: previousMonthSummary?.total_amount || 0,
          previousMonthPercentDiff: ((currentMonthSummary.total_amount - (previousMonthSummary?.total_amount || 0)) / (previousMonthSummary?.total_amount || 0)) * 100,
          previousYear: currentDate.subtract(1, 'year').format('YY'),
          previousYearLabel: fullMonths[currentDate.subtract(1, 'year').month()],
          previousYearAmount: previousYearMonthSummary?.total_amount || 0,
          previousYearPercentDiff: ((currentMonthSummary.total_amount - (previousYearMonthSummary?.total_amount || 0)) / (previousYearMonthSummary?.total_amount || 0)) * 100,
        };
        setTotalCharData(data);
        !loadingData && getMonthLiquidations(data);
      }
    }
  };

  const selectBar = async (event: any) => {
    if (chartRef.current) {
      const elementAtEvent = getElementAtEvent(chartRef.current, event);
      if (elementAtEvent.length > 0) {
        const slicedArray = storedMonthSummaries.slice(currentChartRange.toIdx, currentChartRange.fromIdx + 1).reverse();
        const selectedMonthSummary = slicedArray[getElementAtEvent(chartRef.current, event)[0].index];
        const selectedSummaryDate = dayjs(selectedMonthSummary.date, 'YYYY-MM');
        loadStatsChartData(selectedMonthSummary);
        setSelectedYear(selectedSummaryDate.year().toString());
        setSelectedMonth(fullMonths[selectedSummaryDate.month()]);
        const currentMonthsSlice = storedMonthSummaries.slice(currentChartRange.toIdx, currentChartRange.fromIdx + 1).reverse();
        selectedBarIdx.current = currentMonthsSlice.findIndex((e) => e.date === selectedMonthSummary.date);
      }
    }
  };

  const selectChartMonths = (currentMonth: number, direction: 'prev' | 'next') => {
    const monthsUsed = new Array(5);
    for (let i = 0; i < 5; i++) {
      if (direction === 'next') {
        const monthIndex = (currentMonth - 1 + i) % 12;
        monthsUsed[i] = monthLabelsList[monthIndex];
      } else {
        const monthIndex = (currentMonth - 1 - i + 12) % 12;
        monthsUsed[4 - i] = monthLabelsList[monthIndex];
      }
    }
    return monthsUsed;
  };

  const transformMonthString = (month: number) => {
    if (month < 10) {
      return `0${month}`;
    }
    return month.toString();
  };

  const getMonthLiquidations = async (data: any) => {
    setLoadingData(true);
    const yearSelected = `20${data.actualYear}`;
    const monthSelected = fullMonths.indexOf(data.actualMonthLabel) + 1;
    const settlementsFound = storedSettlements.find((e) => e.year === parseInt(yearSelected) && e.month === fullMonths.indexOf(data.actualMonthLabel) + 1);
    if (settlementsFound) {
      dispatch(selectCurrentSettlements(settlementsFound));
    } else {
      const monthString = transformMonthString(monthSelected);
      const monthDays = getMonthDays(monthSelected, parseInt(`20${data.actualYear}`));
      const params: SettlementsAPIData = {
        offset: 0,
        limit: 30,
        from_date: `&from_date=${yearSelected}-${monthString}-01`,
        to_date: `&to_date=${yearSelected}-${monthString}-${monthDays}`,
      };
      const response = await getSettlements(params);
      const liquidations = {
        month: monthSelected,
        total: response.data.settlements.length,
        year: parseInt(yearSelected),
        list: response.data.settlements,
        more_items: response.metadata?.paging.more_items,
      };
      dispatch(selectCurrentSettlements(liquidations));
      dispatch(saveSettlements([...storedSettlements, liquidations]));
    }
    setLoadingData(false);
  };

  return (
    <IonPage className={styles.reportingPage}>
      <div className={styles.reportingWrapper}>
        <Banner type={BannerType.REPORTING} />
        <div className={styles.reportingMainCard}>
          {isLoading && (
            <div className={styles.reportingLoadingContainer}>
              <div className={styles.customSpinner}>
                <IonSpinner
                  color="danger"
                  name="bubbles"
                  className={styles.scrollSpinner}
                />
                <span className={styles.spinnerText}>Cargando...</span>
              </div>
            </div>
          )}
          <div className={styles.titleWrapper}>
            <p className={styles.titleText}>Reporting</p>
            <hr className={styles.separationLine} />
          </div>
          {!emptyData ? (
            <div className={styles.reportingContent}>
              <div className={styles.reportingChart}>
                <div>
                  <div className={styles.reportingChartTitle}>
                    Gráfico de resumen de movimientos
                  </div>
                  {currentMonthSummary && (
                    <p className={styles.reportingChartAmount}>
                      {pricesHelper(currentMonthSummary.total_amount, true)}
                    </p>
                  )}
                  <p className={styles.reportingChartSubtitle}>Importe abonado</p>
                </div>
                <div className={styles.liquidationMonthSelector}>
                  <div className={styles.monthSelectorBtn} onClick={changeToPreviousMonth}>
                    <img
                      alt="anterior"
                      src={ArrowLeft}
                    />
                  </div>
                  <div className={styles.dateSelector}>
                    <select
                      value={selectedMonth + selectedYear}
                      onChange={handleMonthChange}
                      className={classNames(styles.selectMonth, styles.reportingSelector)}
                    >
                      {availableMonths && availableMonths.map(({ month, year }: any) => (
                        <option key={`${month}-${year}`} value={month + year}>
                          {month}
                        </option>
                      ))}
                    </select>
                    <select
                      value={selectedYear}
                      onChange={handleYearChange}
                      className={classNames(styles.selectYear, styles.reportingSelector)}
                    >
                      {uniqueYears.map((year: any) => (
                        <option disabled={checkDisabledYearOption(year)} key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.monthSelectorBtn} onClick={changeToNextMonth}>
                    <img
                      alt="siguiente"
                      src={ArrowRight}
                    />
                  </div>
                </div>
                <div className={styles.reportingChartBar}>
                  <Bar
                    options={options}
                    data={chartData}
                    ref={chartRef}
                    onClick={selectBar}
                  />
                </div>
              </div>
              <div className={styles.reportingBoxes}>
                <div className={classNames(styles.reportingBox, styles.counterBox)}>
                  <div className={styles.liquidationTitle}>Importe abonado</div>
                  <div className={classNames(styles.reportingBoxRow, styles.currentTotalLiquidation)}>
                    <p className={styles.textSpaceFirst}>
                      {totalCharData.actualMonthLabel}
                      {' '}
                      {totalCharData.actualYear}
                    </p>
                    <p className={styles.textSpace}>
                      {pricesHelper(totalCharData.actualAmount, true)}
                    </p>
                    <div className={styles.badgeSpace} />
                  </div>
                  <hr className={styles.separationLine} />

                  <div className={styles.reportingBoxRow}>
                    <p className={styles.liquidationTextFirst}>
                      {totalCharData.previousMonthLabel}
                      {' '}
                      {totalCharData.previousMonthYear}
                    </p>
                    <p className={styles.liquidationText}>
                      {pricesHelper(totalCharData.previousMonthAmount, true)}
                    </p>
                    <div className={styles.badgeSpace}>
                      {totalCharData.previousMonthAmount !== 0
                        && totalCharData.actualAmount !== 0 && (
                          <div
                            className={
                              percentageHelper(
                                totalCharData.previousMonthAmount,
                                totalCharData.actualAmount,
                              ) > 0
                                ? styles.totalAmountIncrement
                                : styles.totalAmountDecrement
                            }
                          >
                            <img
                              alt="diferencia mes anterior"
                              src={
                                percentageHelper(
                                  totalCharData.previousMonthAmount,
                                  totalCharData.actualAmount,
                                ) > 0
                                  ? ArrowUp
                                  : ArrowDown
                              }
                              className={styles.liquidationArrow}
                            />
                            {Math.abs(
                              percentageHelper(
                                totalCharData.previousMonthAmount,
                                totalCharData.actualAmount,
                              ),
                            )
                              .toFixed(2)
                              .toString()
                              .replace('.', ',')}
                            %
                          </div>
                      )}
                    </div>
                  </div>
                  {totalCharData.previousYearAmount !== 0 && (
                    <>
                      <hr className={styles.separationLine} />
                      <div className={styles.reportingBoxRow}>
                        <p className={styles.liquidationTextFirst}>
                          {totalCharData.previousYearLabel}
                          {' '}
                          {totalCharData.previousYear}
                        </p>
                        <p className={styles.liquidationText}>
                          {totalCharData.previousYearAmount
                            && pricesHelper(totalCharData.previousYearAmount)}
                        </p>
                        <div className={styles.badgeSpace}>
                          {totalCharData.previousYearAmount !== 0 && (
                            <div
                              className={
                                percentageHelper(
                                  totalCharData.previousYearAmount,
                                  totalCharData.actualAmount,
                                ) > 0
                                  ? styles.totalAmountIncrement
                                  : styles.totalAmountDecrement
                              }
                            >
                              <img
                                alt="diferencia año anterior"
                                src={
                                  percentageHelper(
                                    totalCharData.previousYearAmount,
                                    totalCharData.actualAmount,
                                  ) > 0
                                    ? ArrowUp
                                    : ArrowDown
                                }
                                className={styles.liquidationArrow}
                              />
                              {Math.abs(
                                percentageHelper(
                                  totalCharData.previousYearAmount,
                                  totalCharData.actualAmount,
                                ),
                              )
                                .toFixed(2)
                                .toString()
                                .replace('.', ',')}
                              %
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className={classNames(styles.reportingBox, styles.disbursementBox)}>
                  <div className={styles.disbursementTitle}>Operaciones abonadas</div>
                  <div className={styles.disbursementAmount}>
                    {currentMonthSummary
                      && currentMonthSummary.total_loans}
                  </div>
                </div>
                <div className={styles.reportingDetailBtnWrapper}>
                  <IonButtons className={styles.reportingDetailButtons}>
                    {merchantSalesType === 'ECO' && currentSettlements && currentSettlements.list.length > 0
                      && (
                        <IonButton
                          className={classNames('secondaryButton', styles.reportingDetailBtn)}
                          onClick={() => {
                            history.push(`${ROUTES_PATH.MONTH_SETTLEMENT}/${currentSettlements.year}-${transformMonthString(currentSettlements.month)}`);
                          }}
                        >
                          <IonText>Ver liquidaciones del mes</IonText>
                          <div className={styles.reportingDetailIconWrapper}>
                            <IonIcon src={Receipt} className={styles.reportingDetailIcon} />
                          </div>
                        </IonButton>
                      )}
                  </IonButtons>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.reportingEmptySpace}>
              <div>
                <img src={emptyStateImg} alt="no liquidations" />
              </div>
              {!isLoading && (
                <p>
                  Todavía no has realizado ninguna operación y no hay datos que
                  mostrar.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </IonPage>
  );
};

export default Reporting;
